import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ReportFilters } from '../types';

const defaultFilters: ReportFilters = {
  limit: 50,
  page: 0,
};

export function useReportFilters(initialFilters?: Partial<ReportFilters>) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filters from URL search params
  const filters = useMemo<ReportFilters>(() => {
    const parsed: ReportFilters = { ...defaultFilters, ...initialFilters };

    // Parse clientId
    const clientId = searchParams.get('clientId');
    if (clientId) parsed.clientId = Number(clientId);

    // Parse contractId
    const contractId = searchParams.get('contractId');
    if (contractId) parsed.contractId = Number(contractId);

    // Parse matterId
    const matterId = searchParams.get('matterId');
    if (matterId) parsed.matterId = Number(matterId);

    // Parse billingPeriodId
    const billingPeriodId = searchParams.get('billingPeriodId');
    if (billingPeriodId) parsed.billingPeriodId = Number(billingPeriodId);

    // Parse date filters
    const minDate = searchParams.get('endedAt.min');
    if (minDate) parsed['endedAt.min'] = minDate;

    const maxDate = searchParams.get('endedAt.max');
    if (maxDate) parsed['endedAt.max'] = maxDate;

    return parsed;
  }, [searchParams, initialFilters]);

  const updateFilter = useCallback(<K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      if (value === undefined || value === null || value === '') {
        newParams.delete(String(key));
      } else {
        newParams.set(String(key), String(value));
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const resetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const setFilters = useCallback((newFilters: ReportFilters | ((prev: ReportFilters) => ReportFilters)) => {
    setSearchParams((prev) => {
      const currentFilters = filters;
      const updatedFilters = typeof newFilters === 'function' ? newFilters(currentFilters) : newFilters;

      const newParams = new URLSearchParams();

      Object.entries(updatedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' &&
            key !== 'limit' && key !== 'page') { // Don't persist limit/page in URL
          newParams.set(key, String(value));
        }
      });

      return newParams;
    }, { replace: true });
  }, [setSearchParams, filters]);

  return {
    filters,
    updateFilter,
    resetFilters,
    setFilters,
  };
}
